import type { PitchFrame } from '@tunasong/music-lib'
import { createPitchFrameSource } from './pitch-frame-source.js'

/**
 * Run decoded audio through the tracker offline, at the same window and hop the worklet uses.
 *
 * "Extract melody" (spec §5) reads a clip that was never captured against a click, so there is no
 * live stream to listen to - but the pitch layer must be the same one, or a melody extracted from a
 * clip and the same melody sung live would disagree about what was played. Feeding the existing
 * frame source is what guarantees that: the window, the hop and the frame timestamps are its
 * arithmetic, not a second copy of it.
 *
 * Only channel 0 is read. A melody is single-voice (§7) and summing channels would sum two
 * different rooms; the left channel is what the tracker gets on a mono capture anyway.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §5, §8
 */

/** Structurally satisfied by a real `AudioBuffer`, and by a two-field stub in a test. */
export interface DecodableAudio {
  sampleRate: number
  getChannelData: (channel: number) => Float32Array
}

/** The block an `AudioWorklet` delivers. Pushing the same size offline keeps the frames identical. */
const RENDER_QUANTUM = 128

export const decodeToFrames = (audio: DecodableAudio, blockSize: number = RENDER_QUANTUM): PitchFrame[] => {
  const samples = audio.getChannelData(0)
  const source = createPitchFrameSource({ sampleRate: audio.sampleRate })

  const frames: PitchFrame[] = []
  for (let offset = 0; offset < samples.length; offset += blockSize) {
    frames.push(...source.push(samples.subarray(offset, Math.min(offset + blockSize, samples.length))))
  }
  return frames
}
